import type { AdminOrder, OrderAddress } from "@cleanbudget/contract/order";
import { Separator } from "@cleanbudget/ui/components/separator";
import { IconDownload } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import {
	DetailFacts,
	type DetailItem,
	DetailLayout,
	DetailList,
	DetailSection,
} from "@/components/detail-layout";
import { HelpButton } from "@/components/help/help-button";
import { PageHeader } from "@/components/page-header";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { formatCents } from "@/lib/money";
import { CancelSection, HistorySection } from "./-components/history-section";
import { InvoiceSection } from "./-components/invoice-section";
import { OrderStatuses, PaymentStatusBadge } from "./-components/order-badges";
import { orderQuery } from "./-components/order-query";
import { RefundSection } from "./-components/refund-section";
import { TrackingSection } from "./-components/tracking-section";

/**
 * One order (#100): its lines with VAT and the totals, the customer and addresses, the payment,
 * the invoice (#95, #96), its refunds with their credit invoices (#103, #104), shipping with the
 * tracking code (#101, #102), the history with notes, and cancelling an order (a paid one by
 * refunding it in full). Works at phone width: the lines are a list, not a table.
 */
export const Route = createFileRoute("/_admin/order/$orderId")({
	loader: ({ context, params }) => context.queryClient.ensureQueryData(orderQuery(params.orderId)),
	component: OrderPage,
});

function OrderPage() {
	const { t } = useTranslation("order");
	const { orderId } = Route.useParams();
	const { data: order } = useSuspenseQuery(orderQuery(orderId));

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				back={<BackLink to="/order">{t("title")}</BackLink>}
				title={order.orderNumber}
				help="order:order"
				description={
					<span className="flex flex-col gap-2">
						<span>{t("detail.placedAt", { date: formatDateTime(order.placedAt) })}</span>
						<span className="flex flex-wrap items-center gap-1">
							<OrderStatuses order={order} />
							<HelpButton topic="order:statuses" />
						</span>
					</span>
				}
			/>
			<DetailLayout
				aside={
					<>
						<ContactSection order={order} />
						<TrackingSection order={order} />
						<HistorySection order={order} />
						{order.orderStatus === "cancelled" ? null : <CancelSection order={order} />}
					</>
				}
			>
				<LinesSection order={order} />
				<AddressesSection order={order} />
				<PaymentSection order={order} />
				<InvoiceSection order={order} />
				<RefundSection order={order} />
			</DetailLayout>
		</div>
	);
}

function LinesSection({ order }: { order: AdminOrder }) {
	const { t } = useTranslation("order");
	const { totals } = order;
	const basis = [
		order.priceDisplay === "inclVat" ? t("detail.pricesIncl") : t("detail.pricesExcl"),
		order.reverseCharge ? t("detail.reverseCharge") : null,
	]
		.filter(Boolean)
		.join(" ");
	return (
		<DetailSection title={t("detail.lines")} description={basis} help="order:lines">
			<div className="flex flex-col gap-4">
				<ul className="flex flex-col divide-y divide-border" aria-label={t("detail.lines")}>
					{order.lines.map((line) => (
						<li
							key={`${line.variantId}-${line.sku}`}
							className="flex flex-wrap gap-x-4 gap-y-1 py-3 first:pt-0"
						>
							<div className="flex min-w-0 flex-1 basis-56 flex-col">
								<span className="font-medium">{line.productName}</span>
								<span className="text-xs text-muted-foreground">
									{[line.sku, line.options].filter(Boolean).join(" · ")}
								</span>
							</div>
							<div className="flex flex-1 basis-40 items-start justify-between gap-4 tabular-nums sm:justify-end">
								<span className="text-muted-foreground">
									{line.quantity} × {formatCents(line.unitPrice)}
								</span>
								<span className="flex flex-col items-end">
									<span>{formatCents(line.lineTotal)}</span>
									{line.discount > 0 ? (
										<span className="text-xs text-muted-foreground">
											{t("detail.lineDiscount", { amount: formatCents(-line.discount) })}
										</span>
									) : null}
									<span className="text-xs text-muted-foreground">
										{t("detail.vat")} {t("detail.vatRate", { rate: line.vat.vatRate })} ·{" "}
										{formatCents(line.vat.vat)}
									</span>
								</span>
							</div>
						</li>
					))}
				</ul>
				<Separator />
				<dl className="flex flex-col gap-1.5 tabular-nums">
					<Amount label={t("detail.goods")} value={formatCents(totals.goods)} />
					{totals.discount !== null ? (
						<Amount
							label={
								totals.discountCode
									? t("detail.discountWithCode", { code: totals.discountCode })
									: t("detail.discount")
							}
							value={formatCents(-totals.discount)}
						/>
					) : null}
					{totals.shipping !== null ? (
						<Amount
							label={t("detail.shipping")}
							value={totals.shipping === 0 ? t("detail.freeShipping") : formatCents(totals.shipping)}
						/>
					) : null}
					<Amount label={t("detail.totalExcl")} value={formatCents(totals.totalExcl)} />
					{totals.vatRates.map((rate) => (
						<Amount
							key={rate.vatRate}
							label={t("detail.vatAt", { rate: rate.vatRate })}
							value={formatCents(rate.vat)}
							muted
						/>
					))}
					<Amount label={t("detail.totalIncl")} value={formatCents(totals.totalIncl)} strong />
				</dl>
			</div>
		</DetailSection>
	);
}

function Amount({
	label,
	value,
	muted,
	strong,
}: {
	label: ReactNode;
	value: ReactNode;
	muted?: boolean;
	strong?: boolean;
}) {
	return (
		<div
			className={`flex justify-between gap-4 ${muted ? "text-muted-foreground" : ""} ${strong ? "font-semibold" : ""}`}
		>
			<dt>{label}</dt>
			<dd>{value}</dd>
		</div>
	);
}

function AddressesSection({ order }: { order: AdminOrder }) {
	const { t } = useTranslation("order");
	return (
		<DetailSection title={t("detail.addresses")} help="order:addresses">
			<DetailList
				items={[
					{ label: t("detail.shippingAddress"), value: <Address address={order.shippingAddress} /> },
					{ label: t("detail.billingAddress"), value: <Address address={order.billingAddress} /> },
				]}
			/>
		</DetailSection>
	);
}

function Address({ address }: { address: OrderAddress }) {
	const { t } = useTranslation("order");
	const number = [address.houseNumber, address.houseNumberAddition].filter(Boolean).join(" ");
	return (
		<address className="not-italic">
			{address.company ? (
				<>
					<span className="font-medium">{address.company}</span>
					<br />
					{t("detail.recipient", { name: address.recipientName })}
				</>
			) : (
				address.recipientName
			)}
			<br />
			{address.street} {number}
			<br />
			{address.postcode} {address.city}
			<br />
			<span className="text-muted-foreground">{t(`detail.country.${address.country}`)}</span>
		</address>
	);
}

function ContactSection({ order }: { order: AdminOrder }) {
	const { t } = useTranslation("order");
	return (
		<DetailSection
			title={t("detail.contact")}
			help="order:customer"
			actions={
				<Link
					to="/customer/$customerId"
					params={{ customerId: order.customerId }}
					className="text-sm underline-offset-4 hover:underline"
				>
					{t("detail.toCustomer")}
				</Link>
			}
		>
			<DetailFacts
				facts={[
					{ label: t("detail.name"), value: `${order.firstName} ${order.lastName}` },
					{ label: t("detail.email"), value: <a href={`mailto:${order.email}`}>{order.email}</a> },
					{
						label: t("detail.phone"),
						value: order.phone ? (
							<a href={`tel:${order.phone}`}>{order.phone}</a>
						) : (
							<span className="text-muted-foreground">{t("detail.noPhone")}</span>
						),
					},
				]}
			/>
		</DetailSection>
	);
}

function PaymentSection({ order }: { order: AdminOrder }) {
	const { t } = useTranslation("order");
	return (
		<DetailSection
			title={t("payment.title")}
			help="order:payment"
			actions={<PaymentStatusBadge status={order.paymentStatus} />}
		>
			<div className="flex flex-col gap-4">
				<DetailList
					items={[
						{ label: t("payment.method"), value: t(`paymentMethod.${order.paymentMethod}`) },
						...(order.paidAt ? [{ label: t("payment.paidAt"), value: formatDateTime(order.paidAt) }] : []),
						...(order.expiresAt
							? [{ label: t("payment.expiresAt"), value: formatDateTime(order.expiresAt) }]
							: []),
						// Cancelled on account (spec, "Betaalpaneel van een geannuleerde order op rekening"): the
						// invoice is credited, so there is nothing to pay and no due date.
						...(order.cancellationCredit
							? [
									{
										label: t("payment.credited"),
										value: (
											<span className="flex flex-wrap items-center gap-2">
												<span>{order.cancellationCredit.number}</span>
												<a
													href={order.cancellationCredit.pdfUrl}
													download={`creditfactuur-${order.cancellationCredit.number}.pdf`}
													className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
												>
													<IconDownload className="size-4" aria-hidden />
													{t("payment.creditDownload")}
												</a>
											</span>
										),
									},
								]
							: order.dueOn
								? [
										{
											label: t("payment.dueOn"),
											help: "order:onAccount",
											value: (
												<span className="flex flex-wrap items-center gap-2">
													<time dateTime={order.dueOn}>{formatCalendarDate(order.dueOn)}</time>
													{order.paymentStatus === "on_account" ? (
														<Link to="/order/invoices" className="text-sm underline-offset-4 hover:underline">
															{t("payment.openInvoices")}
														</Link>
													) : null}
												</span>
											),
										} satisfies DetailItem,
									]
								: []),
					]}
				/>
				<div className="flex flex-col gap-2">
					<h3 className="text-muted-foreground">{t("payment.transactions")}</h3>
					{order.payments.length === 0 ? (
						<p className="text-muted-foreground">{t("payment.noTransactions")}</p>
					) : (
						<ul className="flex flex-col gap-2" aria-label={t("payment.transactions")}>
							{order.payments.map((payment) => (
								<li key={payment.id} className="flex flex-col gap-0.5 rounded-lg border border-border p-3">
									<span>
										{t("payment.transaction", {
											method: t(`paymentMethod.${payment.method}`),
											amount: formatCents(payment.amount),
											status: t(`payment.transactionStatus.${payment.status}`),
										})}
									</span>
									<span className="text-xs break-all text-muted-foreground">
										{[
											payment.paynlOrderId ? t("payment.transactionId", { id: payment.paynlOrderId }) : null,
											t("payment.startedAt", { date: formatDateTime(payment.createdAt) }),
											payment.testMode ? t("payment.testMode") : null,
										]
											.filter(Boolean)
											.join(" · ")}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</DetailSection>
	);
}
